import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import types from '../../mutation-types';
import ContactAPI from '../../../api/contacts';
import snakecaseKeys from 'snakecase-keys';
import AccountActionsAPI from '../../../api/accountActions';
import AnalyticsHelper from '../../../helper/AnalyticsHelper';
import { CONTACTS_EVENTS } from '../../../helper/AnalyticsHelper/events';
import axios from 'axios';

// Simple cache implementation with TTL
const filterCache = {
  cache: new Map(),
  ttl: 30000, // 30 seconds cache TTL

  get(key) {
    const item = this.cache.get(key);
    if (item && Date.now() - item.timestamp < this.ttl) {
      return item.data;
    }
    // Remove expired item
    this.cache.delete(key);
    return null;
  },

  set(key, data) {
    // Limit cache size to prevent memory issues
    if (this.cache.size >= 50) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, { data, timestamp: Date.now() });
  },

  clear() {
    this.cache.clear();
  },
};

// Request manager to handle cancellation and deduplication
const requestManager = {
  pendingRequests: new Map(),

  async executeRequest(key, requestFn) {
    // Cancel any pending request with same key
    if (this.pendingRequests.has(key)) {
      const source = this.pendingRequests.get(key);
      source.cancel('Request cancelled due to new request');
    }

    // Create new cancelable request
    const source = axios.CancelToken.source();
    this.pendingRequests.set(key, source);

    try {
      const result = await requestFn(source.token);
      this.pendingRequests.delete(key);
      return result;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.pendingRequests.delete(key);
        throw error;
      }
      return null;
    }
  },

  cancelAll() {
    this.pendingRequests.forEach(source => {
      source.cancel('All requests cancelled');
    });
    this.pendingRequests.clear();
  },
};

const buildContactFormData = contactParams => {
  const formData = new FormData();
  const { additional_attributes = {}, ...contactProperties } = contactParams;
  Object.keys(contactProperties).forEach(key => {
    if (contactProperties[key]) {
      formData.append(key, contactProperties[key]);
    }
  });
  const { social_profiles, ...additionalAttributesProperties } =
    additional_attributes;
  Object.keys(additionalAttributesProperties).forEach(key => {
    formData.append(
      `additional_attributes[${key}]`,
      additionalAttributesProperties[key]
    );
  });
  Object.keys(social_profiles).forEach(key => {
    formData.append(
      `additional_attributes[social_profiles][${key}]`,
      social_profiles[key]
    );
  });
  return formData;
};

export const handleContactOperationErrors = error => {
  if (error.response?.status === 422) {
    throw new DuplicateContactException(error.response.data.attributes);
  } else if (error.response?.data?.message) {
    throw new ExceptionWithMessage(error.response.data.message);
  } else {
    throw new Error(error);
  }
};

export const actions = {
  search: async ({ commit }, { search, page, sortAttr, label }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.search(search, page, sortAttr, label);
      commit(types.CLEAR_CONTACTS);
      commit(types.SET_CONTACTS, payload);
      commit(types.SET_CONTACT_META, meta);
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
  },

  get: async ({ commit }, { page = 1, sortAttr, label } = {}) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.get(page, sortAttr, label);
      commit(types.CLEAR_CONTACTS);
      commit(types.SET_CONTACTS, payload);
      commit(types.SET_CONTACT_META, meta);
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
  },

  show: async ({ commit }, { id }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await ContactAPI.show(id);
      commit(types.SET_CONTACT_ITEM, response.data.payload);
      commit(types.SET_CONTACT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },

  update: async ({ commit }, { id, isFormData = false, ...contactParams }) => {
    const { avatar, customAttributes, ...paramsToDecamelize } = contactParams;
    const decamelizedContactParams = {
      ...snakecaseKeys(paramsToDecamelize, { deep: true }),
      ...(customAttributes && { custom_attributes: customAttributes }),
      ...(avatar && { avatar }),
    };
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ContactAPI.update(
        id,
        isFormData
          ? buildContactFormData(decamelizedContactParams)
          : decamelizedContactParams
      );
      commit(types.EDIT_CONTACT, response.data.payload);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      // Clear cache after successful update to ensure fresh data
      filterCache.clear();
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      handleContactOperationErrors(error);
    }
  },

  create: async ({ commit }, { isFormData = false, ...contactParams }) => {
    const decamelizedContactParams = snakecaseKeys(contactParams, {
      deep: true,
    });
    commit(types.SET_CONTACT_UI_FLAG, { isCreating: true });
    try {
      const response = await ContactAPI.create(
        isFormData
          ? buildContactFormData(decamelizedContactParams)
          : decamelizedContactParams
      );

      AnalyticsHelper.track(CONTACTS_EVENTS.CREATE_CONTACT);
      commit(types.SET_CONTACT_ITEM, response.data.payload.contact);
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return response.data.payload.contact;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return handleContactOperationErrors(error);
    }
  },

  import: async ({ commit }, file) => {
    commit(types.SET_CONTACT_UI_FLAG, { isImporting: true });
    try {
      await ContactAPI.importContacts(file);
      commit(types.SET_CONTACT_UI_FLAG, { isImporting: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isImporting: false });
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      }
    }
  },

  export: async ({ commit }, { payload, label }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isExporting: true });
    try {
      await ContactAPI.exportContacts({ payload, label });

      commit(types.SET_CONTACT_UI_FLAG, { isExporting: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isExporting: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_CONTACT_UI_FLAG, { isDeleting: true });
    try {
      await ContactAPI.delete(id);
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },

  deleteCustomAttributes: async ({ commit }, { id, customAttributes }) => {
    try {
      const response = await ContactAPI.destroyCustomAttributes(
        id,
        customAttributes
      );
      commit(types.EDIT_CONTACT, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteAvatar: async ({ commit }, id) => {
    try {
      const response = await ContactAPI.destroyAvatar(id);
      commit(types.EDIT_CONTACT, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  fetchContactableInbox: async ({ commit }, id) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetchingInboxes: true });
    try {
      const response = await ContactAPI.getContactableInboxes(id);
      const contact = {
        id: Number(id),
        contact_inboxes: response.data.payload,
      };
      commit(types.SET_CONTACT_ITEM, contact);
    } catch (error) {
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      } else {
        throw new Error(error);
      }
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isFetchingInboxes: false });
    }
  },

  updatePresence: ({ commit }, data) => {
    commit(types.UPDATE_CONTACTS_PRESENCE, data);
  },

  setContact({ commit }, data) {
    commit(types.SET_CONTACT_ITEM, data);
  },

  merge: async ({ commit }, { childId, parentId }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isMerging: true });
    try {
      const response = await AccountActionsAPI.merge(parentId, childId);
      commit(types.SET_CONTACT_ITEM, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isMerging: false });
    }
  },

  deleteContactThroughConversations: ({ commit }, id) => {
    commit(types.DELETE_CONTACT, id);
    commit(types.CLEAR_CONTACT_CONVERSATIONS, id, { root: true });
    commit(`contactConversations/${types.DELETE_CONTACT_CONVERSATION}`, id, {
      root: true,
    });
  },

  updateContact: async ({ commit }, updateObj) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      commit(types.EDIT_CONTACT, updateObj);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    }
  },

  filter: async (
    { commit },
    { page = 1, sortAttr, queryPayload, resetState = true } = {}
  ) => {
    // Debug logging for Vuex filter action (controlled by RESPONZA_DEBUG)
    const isDebugEnabled = window.chatwootConfig?.responzaDebug === 'true' || process.env?.RESPONZA_DEBUG === 'true';
    
    if (isDebugEnabled) {
      console.group('[RESPONZA_DEBUG] Vuex contacts/filter action');
      console.log('Timestamp:', new Date().toISOString());
      console.log('Page:', page);
      console.log('Sort Attr:', sortAttr);
      console.log('Reset State:', resetState);
      console.log('Query Payload:', JSON.stringify(queryPayload));
      
      // Check for phone number patterns
      const payloadStr = JSON.stringify(queryPayload);
      if (payloadStr && payloadStr.match(/\d{10,15}/)) {
        console.error('ALERT: Phone number pattern in Vuex filter!');
        console.error('Possible bulk message automation detected');
        
        // Log if there are duplicate phone number formats
        if (payloadStr.includes('5511') && payloadStr.includes('551')) {
          console.error('CRITICAL: Duplicate phone number format pattern detected!');
          console.error('This matches the incident report pattern (both 5511xxx and 5511xxx formats)');
        }
      }
      
      // Track call frequency
      if (!window.responzaDebugFilterCalls) {
        window.responzaDebugFilterCalls = [];
      }
      const now = Date.now();
      window.responzaDebugFilterCalls.push(now);
      
      // Keep only calls from last minute
      window.responzaDebugFilterCalls = window.responzaDebugFilterCalls.filter(
        time => now - time < 60000
      );
      
      console.log('Filter calls in last minute:', window.responzaDebugFilterCalls.length);
      if (window.responzaDebugFilterCalls.length > 50) {
        console.error('HIGH FREQUENCY WARNING: More than 50 filter calls in the last minute!');
      }
    }
    
    // Create a unique key for this request
    const cacheKey = `filter-${JSON.stringify(queryPayload)}-${page}-${sortAttr}`;

    // Check cache first for instant response
    const cached = filterCache.get(cacheKey);
    if (cached) {
      if (isDebugEnabled) {
        console.log('CACHE HIT! Returning cached data');
        console.log('Cache key:', cacheKey);
        console.log('Cached items count:', cached.payload?.length || 0);
        console.groupEnd();
      }
      
      if (resetState) {
        commit(types.CLEAR_CONTACTS);
        commit(types.SET_CONTACTS, cached.payload);
        commit(types.SET_CONTACT_META, cached.meta);
      }
      return cached.payload;
    }
    
    if (isDebugEnabled) {
      console.log('CACHE MISS - Making API request');
      console.log('Cache key:', cacheKey);
    }

    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });

    const result = await requestManager.executeRequest(
      cacheKey,
      async cancelToken => {
        try {
          if (isDebugEnabled) {
            console.log('Executing API request with cancel token');
          }
          
          const {
            data: { payload, meta },
          } = await ContactAPI.filter(
            page,
            sortAttr,
            queryPayload,
            cancelToken
          );
          
          if (isDebugEnabled) {
            console.log('API request successful');
            console.log('Results count:', payload?.length || 0);
            console.log('Total count from meta:', meta?.count || 0);
          }

          // Cache the successful response
          filterCache.set(cacheKey, { payload, meta });

          if (resetState) {
            commit(types.CLEAR_CONTACTS);
            commit(types.SET_CONTACTS, payload);
            commit(types.SET_CONTACT_META, meta);
            commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
          }
          return payload;
        } catch (error) {
          if (isDebugEnabled) {
            if (axios.isCancel(error)) {
              console.warn('Request was cancelled');
            } else {
              console.error('API request failed:', error);
            }
          }
          
          if (!axios.isCancel(error)) {
            commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
          }
          return [];
        }
      }
    );
    
    if (isDebugEnabled) {
      console.log('Final result:', result?.length || 0, 'contacts');
      console.groupEnd();
    }

    return result || [];
  },

  setContactFilters({ commit }, data) {
    commit(types.SET_CONTACT_FILTERS, data);
  },

  clearContactFilters({ commit }) {
    commit(types.CLEAR_CONTACT_FILTERS);
  },
};
